/**
 * Unified column schema registry for ranks table modes.
 * This centralizes column IDs, labels, sort behavior, and semantic classes.
 */

/**
 * @typedef {"template"|"component"|"plugin"|"vector"|"site"|"tutorial"|"creator"|"expert"} TableType
 */

import { getMedalSortScore } from "./core.js";

const DEFAULT_COLUMN_FEATURE_FLAGS = {
  topPercentColumn: true,
};

let columnFeatureFlags = { ...DEFAULT_COLUMN_FEATURE_FLAGS };

const DELTA_HEADER_TOOLTIP_TEXT = "Rank change since yesterday";

function renderDeltaHeaderLabel(labelText, { nativeTitle = false } = {}) {
  if (nativeTitle) {
    return `<span class="sortable-header" title="${DELTA_HEADER_TOOLTIP_TEXT}">${labelText}</span>`;
  }
  return `<span class="with-tooltip change-header-tooltip"><span class="sortable-header">${labelText}</span><div class="tooltip right"><div class="tooltip-item">${DELTA_HEADER_TOOLTIP_TEXT}</div></div></span>`;
}

export function setColumnFeatureFlags(flags = {}) {
  columnFeatureFlags = {
    ...DEFAULT_COLUMN_FEATURE_FLAGS,
    topPercentColumn: flags.topPercentColumn !== false,
  };
}

const STANDARD_COLUMNS = [
  {
    id: "rank",
    label: "Rank",
    modeVisibility: ["template", "component", "plugin", "vector", "site", "tutorial"],
    sortable: true,
    defaultSortDirection: "asc",
    headerClass: "col-rank",
    cellClass: "col-rank",
    footerClass: "col-rank",
  },
  {
    id: "change",
    labelHtml: renderDeltaHeaderLabel("1d Δ"),
    modeVisibility: ["template", "component", "plugin", "vector", "site"],
    sortable: true,
    defaultSortDirection: "desc",
    headerClass: "col-change",
    cellClass: "col-change",
    footerClass: "col-change",
  },
  {
    id: "toppercent",
    label: "Top %",
    labelHtml: '<span>Top %</span> <span class="header-new-pill">New!</span>',
    modeVisibility: ["__hidden__"],
    sortable: false,
    defaultSortDirection: "asc",
    headerClass: "col-toppercent",
    cellClass: "col-toppercent",
    footerClass: "col-toppercent",
  },
  {
    id: "trend",
    label: "7-day Trend",
    modeVisibility: ["template", "component", "plugin", "vector", "site"],
    sortable: false,
    defaultSortDirection: "asc",
    headerClass: "col-trend",
    cellClass: "col-trend",
    footerClass: "col-trend",
  },
  {
    id: "price",
    label: "Price",
    modeVisibility: ["template", "component", "plugin", "vector"],
    sortable: true,
    defaultSortDirection: "asc",
    headerClass: "col-price",
    cellClass: "col-price",
    footerClass: "col-price",
  },
  {
    id: "image",
    label: "Img",
    modeVisibility: ["template", "component", "plugin", "vector", "site"],
    sortable: false,
    defaultSortDirection: "asc",
    headerClass: "col-img image-column",
    cellClass: "col-img image-column",
    footerClass: "col-img image-column",
  },
  {
    id: "name",
    label: "Name",
    modeVisibility: ["template", "component", "plugin", "vector", "site", "tutorial"],
    sortable: true,
    defaultSortDirection: "asc",
    headerClass: "col-name",
    cellClass: "col-name",
    footerClass: "col-name",
  },
  {
    id: "medals",
    label: "Medals",
    labelHtml: '<span class="sortable-header">Medals</span>',
    modeVisibility: ["__hidden__"],
    sortable: true,
    defaultSortDirection: "desc",
    headerClass: "col-medals",
    cellClass: "col-medals",
    footerClass: "col-medals",
  },
  {
    id: "categories",
    labelHtml: '<span class="sortable-header">Categories</span>',
    modeVisibility: ["template", "component", "plugin", "vector", "site"],
    sortable: true,
    defaultSortDirection: "desc",
    headerClass: "col-categories",
    cellClass: "col-categories",
    footerClass: "col-categories",
  },
  {
    id: "author",
    label: "Creator",
    modeVisibility: ["template", "component", "plugin", "vector", "site", "tutorial"],
    sortable: true,
    defaultSortDirection: "asc",
    headerClass: "col-author",
    cellClass: "col-author",
    footerClass: "col-author",
  },
];

const CREATOR_COLUMNS = [
  {
    id: "name",
    label: "Name",
    modeVisibility: ["creator"],
    sortable: true,
    defaultSortDirection: "asc",
    headerClass: "creators-col-name col-creator-name",
    cellClass: "creator-cell-name creators-col-name col-creator-name",
    footerClass: "col-creator-name",
  },
  {
    id: "templates",
    label: "Templates",
    modeVisibility: ["creator"],
    sortable: true,
    defaultSortDirection: "desc",
    headerClass: "creators-col-metric col-creator-templates",
    cellClass: "creator-cell-number creators-col-metric col-creator-templates",
    footerClass: "col-creator-templates",
  },
  {
    id: "templatesdelta",
    labelHtml: renderDeltaHeaderLabel("1d Δ", { nativeTitle: true }),
    modeVisibility: ["creator"],
    sortable: true,
    defaultSortDirection: "desc",
    headerClass: "creators-col-delta col-creator-templates-delta",
    cellClass: "creator-cell-number creators-col-delta col-creator-templates-delta",
    footerClass: "col-creator-templates-delta",
  },
  {
    id: "components",
    label: "Components",
    modeVisibility: ["creator"],
    sortable: true,
    defaultSortDirection: "desc",
    headerClass: "creators-col-metric col-creator-components",
    cellClass: "creator-cell-number creators-col-metric col-creator-components",
    footerClass: "col-creator-components",
  },
  {
    id: "componentsdelta",
    labelHtml: renderDeltaHeaderLabel("1d Δ", { nativeTitle: true }),
    modeVisibility: ["creator"],
    sortable: true,
    defaultSortDirection: "desc",
    headerClass: "creators-col-delta col-creator-components-delta",
    cellClass: "creator-cell-number creators-col-delta col-creator-components-delta",
    footerClass: "col-creator-components-delta",
  },
  {
    id: "plugins",
    label: "Plugins",
    modeVisibility: ["creator"],
    sortable: true,
    defaultSortDirection: "desc",
    headerClass: "creators-col-metric col-creator-plugins",
    cellClass: "creator-cell-number creators-col-metric col-creator-plugins",
    footerClass: "col-creator-plugins",
  },
  {
    id: "pluginsdelta",
    labelHtml: renderDeltaHeaderLabel("1d Δ", { nativeTitle: true }),
    modeVisibility: ["creator"],
    sortable: true,
    defaultSortDirection: "desc",
    headerClass: "creators-col-delta col-creator-plugins-delta",
    cellClass: "creator-cell-number creators-col-delta col-creator-plugins-delta",
    footerClass: "col-creator-plugins-delta",
  },
  {
    id: "vectors",
    label: "Vectors",
    modeVisibility: ["creator"],
    sortable: true,
    defaultSortDirection: "desc",
    headerClass: "creators-col-metric col-creator-vectors",
    cellClass: "creator-cell-number creators-col-metric col-creator-vectors",
    footerClass: "col-creator-vectors",
  },
  {
    id: "vectorsdelta",
    labelHtml: renderDeltaHeaderLabel("1d Δ", { nativeTitle: true }),
    modeVisibility: ["creator"],
    sortable: true,
    defaultSortDirection: "desc",
    headerClass: "creators-col-delta col-creator-vectors-delta",
    cellClass: "creator-cell-number creators-col-delta col-creator-vectors-delta",
    footerClass: "col-creator-vectors-delta",
  },
  {
    id: "tutorials",
    label: "Tutorials",
    modeVisibility: ["creator"],
    sortable: true,
    defaultSortDirection: "desc",
    headerClass: "creators-col-metric col-creator-tutorials",
    cellClass: "creator-cell-number creators-col-metric col-creator-tutorials",
    footerClass: "col-creator-tutorials",
  },
  {
    id: "tutorialsdelta",
    labelHtml: renderDeltaHeaderLabel("1d Δ", { nativeTitle: true }),
    modeVisibility: ["creator"],
    sortable: true,
    defaultSortDirection: "desc",
    headerClass: "creators-col-delta col-creator-tutorials-delta",
    cellClass: "creator-cell-number creators-col-delta col-creator-tutorials-delta",
    footerClass: "col-creator-tutorials-delta",
  },
  {
    id: "total",
    label: "Total",
    modeVisibility: ["creator"],
    sortable: true,
    defaultSortDirection: "desc",
    headerClass: "creators-col-metric col-creator-total",
    cellClass: "creator-cell-number creators-col-metric col-creator-total",
    footerClass: "col-creator-total",
  },
  {
    id: "totaldelta",
    labelHtml: renderDeltaHeaderLabel("1d Δ", { nativeTitle: true }),
    modeVisibility: ["creator"],
    sortable: true,
    defaultSortDirection: "desc",
    headerClass: "creators-col-delta col-creator-total-delta",
    cellClass: "creator-cell-number creators-col-delta col-creator-total-delta",
    footerClass: "col-creator-total-delta",
  },
];

const EXPERT_COLUMNS = [
  {
    id: "rank",
    label: "Rank",
    modeVisibility: ["expert"],
    sortable: true,
    defaultSortDirection: "asc",
    headerClass: "col-rank",
    cellClass: "col-rank",
    footerClass: "col-rank",
  },
  {
    id: "change",
    labelHtml: renderDeltaHeaderLabel("1d Δ"),
    modeVisibility: ["expert"],
    sortable: true,
    defaultSortDirection: "desc",
    headerClass: "col-change",
    cellClass: "col-change",
    footerClass: "col-change",
  },
  {
    id: "image",
    label: "Img",
    modeVisibility: ["expert"],
    sortable: false,
    defaultSortDirection: "asc",
    headerClass: "col-img image-column",
    cellClass: "col-img image-column",
    footerClass: "col-img image-column",
  },
  {
    id: "name",
    label: "Name",
    modeVisibility: ["expert"],
    sortable: true,
    defaultSortDirection: "asc",
    headerClass: "col-name",
    cellClass: "col-name",
    footerClass: "col-name",
  },
  {
    id: "expertType",
    label: "Type",
    modeVisibility: ["expert"],
    sortable: true,
    defaultSortDirection: "asc",
    headerClass: "col-expert-type",
    cellClass: "col-expert-type",
    footerClass: "col-expert-type",
  },
  {
    id: "expertPro",
    label: "Pro",
    modeVisibility: ["expert"],
    sortable: true,
    defaultSortDirection: "desc",
    headerClass: "col-expert-pro",
    cellClass: "col-expert-pro",
    footerClass: "col-expert-pro",
  },
  {
    id: "country",
    label: "Country",
    modeVisibility: ["expert"],
    sortable: true,
    defaultSortDirection: "asc",
    headerClass: "col-expert-country",
    cellClass: "col-expert-country",
    footerClass: "col-expert-country",
  },
];

export function getCategoryDisplayMode(type, items = undefined) {
  const normalizedType = type || "template";
  if (!Array.isArray(items)) return "single";

  const typeItems = items.filter((item) => (item?.type || "template") === normalizedType);
  if (normalizedType === "template" && typeItems.some((item) => Object.hasOwn(item, "mainCategory"))) {
    return "main-category-rank";
  }
  const maxCategoryCount = typeItems.reduce(
    (maximum, item) => Math.max(maximum, Array.isArray(item?.categories) ? item.categories.length : 0),
    0,
  );
  if (maxCategoryCount > 3) return "count";
  if (maxCategoryCount > 1) return "stacked";
  return "single";
}

function getColumnsInternal(type, options = {}) {
  if (type === "creator") return CREATOR_COLUMNS;
  if (type === "expert") return EXPERT_COLUMNS;
  const columns = STANDARD_COLUMNS.filter((column) => {
    if (column.id === "toppercent" && columnFeatureFlags.topPercentColumn === false) return false;
    if (!Array.isArray(column.modeVisibility) || column.modeVisibility.length === 0) return true;
    return column.modeVisibility.includes(type);
  });
  return columns.map((column) => {
    if (column.id === "categories") {
      const categoryDisplayMode = options.categoryDisplayMode || getCategoryDisplayMode(type);
      return {
        ...column,
        labelHtml:
          categoryDisplayMode === "count" || categoryDisplayMode === "stacked"
            ? '<span class="sortable-header">Categories</span>'
            : '<span class="sortable-header">Category</span>',
      };
    }
    return column;
  });
}

export function getColumnsForType(type, options = {}) {
  return [...getColumnsInternal(type, options)];
}

export function getSortableColumnsForType(type) {
  return new Set(
    getColumnsInternal(type)
      .filter((column) => column.sortable)
      .map((column) => column.id),
  );
}

export function getDefaultSortForType(type) {
  const columns = getColumnsInternal(type);
  const preferred = type === "creator" ? "total" : "rank";
  const preferredColumn = columns.find((column) => column.id === preferred && column.sortable);
  if (preferredColumn) {
    return {
      sortColumn: preferredColumn.id,
      sortDirection: preferredColumn.defaultSortDirection || "asc",
    };
  }

  const firstSortable = columns.find((column) => column.sortable);
  if (!firstSortable) return { sortColumn: "rank", sortDirection: "asc" };
  return {
    sortColumn: firstSortable.id,
    sortDirection: firstSortable.defaultSortDirection || "asc",
  };
}

export function getDefaultDirection(type, columnId) {
  const column = getColumnsInternal(type).find((item) => item.id === columnId);
  return column?.defaultSortDirection || "asc";
}

export function getColumnSortValue(item, column, context = "all") {
  if (item?.type === "expert") {
    const rankEntry = item.ranks?.[context] || item.ranks?.["global-all"] || [item.rank, item.change];
    if (column === "rank") return Number(rankEntry?.[0]) || null;
    if (column === "change") {
      const change = rankEntry?.[1] ?? null;
      if (change === "new") return 1000003;
      if (change === "moved-in") return 1000002;
      if (change === "comeback") return 1000001;
      if (change === "price_change") return 1000000;
      if (typeof change === "number") return change;
      return -Infinity;
    }
    if (column === "name") return String(item.name || "").toLowerCase();
    if (column === "expertType") {
      const types = Array.isArray(item.expertTypes) ? item.expertTypes : [];
      return types.find((type) => type === "studio" || type === "freelancer") || "";
    }
    if (column === "expertPro") return Array.isArray(item.expertTypes) && item.expertTypes.includes("pro") ? 1 : 0;
    if (column === "country") {
      const code = String(item.country || "")
        .trim()
        .toUpperCase();
      try {
        return String(new Intl.DisplayNames(["en"], { type: "region" }).of(code) || code).toLowerCase();
      } catch {
        return code.toLowerCase();
      }
    }
    return null;
  }
  if (item?.type === "creator") {
    const creatorStats = item.creatorStats || {};
    switch (column) {
      case "rank":
        return null;
      case "name":
        return String(item.name || "").toLowerCase();
      case "templates":
        return creatorStats.totalTemplates ?? 0;
      case "templatesdelta":
        return creatorStats.totalTemplatesDelta1d ?? null;
      case "components":
        return creatorStats.totalComponents ?? 0;
      case "componentsdelta":
        return creatorStats.totalComponentsDelta1d ?? null;
      case "plugins":
        return creatorStats.totalPlugins ?? 0;
      case "pluginsdelta":
        return creatorStats.totalPluginsDelta1d ?? null;
      case "vectors":
        return creatorStats.totalVectors ?? 0;
      case "vectorsdelta":
        return creatorStats.totalVectorsDelta1d ?? null;
      case "tutorials":
        return creatorStats.totalTutorials ?? 0;
      case "tutorialsdelta":
        return creatorStats.totalTutorialsDelta1d ?? null;
      case "total":
        return creatorStats.totalItems ?? 0;
      case "totaldelta":
        return creatorStats.totalItemsDelta1d ?? null;
      case "medals":
        return 0;
      case "author":
        return "";
      default:
        return null;
    }
  }

  switch (column) {
    case "rank": {
      const entry = item.ranks?.[context];
      return entry ? entry[0] : null;
    }
    case "change": {
      const entry = item.ranks?.[context];
      const change = entry ? entry[1] : null;
      if (change === "new") return 1000003;
      if (change === "moved-in") return 1000002;
      if (change === "comeback") return 1000001;
      if (change === "price_change") return 1000000;
      if (typeof change === "number") return change;
      return -Infinity;
    }
    case "price": {
      if (item.price === "Free") return -1;
      const priceNum = parseFloat(String(item.price).replace(/[^0-9.]/g, "")) || 0;
      return priceNum;
    }
    case "name":
      return String(item.name || "").toLowerCase();
    case "categories": {
      const categories = Array.isArray(item.categories) ? item.categories : [];
      if ((item?.type || "template") === "template") {
        const mainCategory = typeof item.mainCategory === "string" ? item.mainCategory.trim() : "";
        return mainCategory ? mainCategory.toLowerCase() : null;
      }
      if (categories.length === 0) return "";
      return String(categories[0] || "").toLowerCase();
    }
    case "medals":
      return getMedalSortScore(item);
    case "author":
      return String(item.authorName || "").toLowerCase();
    default:
      return null;
  }
}
